/**
 * Horizontal Source List Component
 * Displays sources in a horizontally scrollable list with navigation arrows
 */

import '../../../../styles/oc-utilities.css';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { MessageReference } from '../../../Message/types/messages';
import {
  ArrowIcon,
  Container,
  LeftArrowButton,
  RightArrowButton,
  ScrollContainer,
  SourceItem,
  SourcesGrid,
} from './HorizontalSourceList.styled';
import SourceCard from './SourceCard';

export type HorizontalSourceListProps = {
  messageId: string;
  sources: MessageReference[];
  isExpanded?: boolean;
  className?: string;
  'aria-labelledby'?: string;
  id?: string;
  role?: string;
};

const SOURCE_WIDTH = 285;
const SOURCE_GAP = 16;

/**
 * HorizontalSourceList Component
 * Displays a horizontally scrollable list of source cards with navigation arrows
 */
const HorizontalSourceList: React.FC<HorizontalSourceListProps> = ({
  messageId,
  sources,
  isExpanded = true,
  className = '',
  'aria-labelledby': ariaLabelledBy,
  id,
  role,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Check scroll position and update arrow visibility
  const updateArrowVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const contentWidth = (SOURCE_WIDTH + SOURCE_GAP) * sources.length - SOURCE_GAP;

    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1 && contentWidth > clientWidth);
  }, [sources.length]);

  // Scroll left by one card width
  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTo({
      left: container.scrollLeft - SOURCE_WIDTH,
      behavior: 'smooth',
    });
  }, []);

  // Scroll right by one card width
  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTo({
      left: container.scrollLeft + SOURCE_WIDTH,
      behavior: 'smooth',
    });
  }, []);

  // Update arrow visibility on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      requestAnimationFrame(updateArrowVisibility);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [updateArrowVisibility]);

  // Initial arrow visibility check
  useEffect(() => {
    if (isExpanded) {
      updateArrowVisibility();
    }
  }, [isExpanded, updateArrowVisibility, sources]);

  // Resize observer to update on window resize
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      updateArrowVisibility();
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [updateArrowVisibility]);

  if (!isExpanded) {
    return null;
  }

  return (
    <Container
      className={className}
      id={id}
      role={role}
      aria-labelledby={ariaLabelledBy}
      data-qa-id="horizontal-source-list"
    >
      {/* Left arrow */}
      {showLeftArrow && (
        <LeftArrowButton type="button" onClick={scrollLeft} aria-hidden="true" tabIndex={-1}>
          <ArrowIcon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </ArrowIcon>
        </LeftArrowButton>
      )}

      {/* Scrollable container */}
      <ScrollContainer ref={scrollContainerRef}>
        <SourcesGrid>
          {sources.map((source, index) => (
            <SourceItem key={`${messageId}-source-${index}`}>
              <SourceCard source={source} ordinal={index + 1} />
            </SourceItem>
          ))}
        </SourcesGrid>
      </ScrollContainer>

      {/* Right arrow */}
      {showRightArrow && (
        <RightArrowButton type="button" onClick={scrollRight} aria-hidden="true" tabIndex={-1}>
          <ArrowIcon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </ArrowIcon>
        </RightArrowButton>
      )}
    </Container>
  );
};

export default React.memo(HorizontalSourceList);
